/**
 * W48 — Marketing Dashboard page at /dashboards/marketing
 *
 * Renders within the W46 /dashboards shell pattern (tab nav, OperatorShell).
 * Six components:
 *   (A) FunnelVisual
 *   (B) MarketingCalendar
 *   (C) HookLibrary
 *   (D) LinkedInDripCard
 *   (E) MarketingActivityAudit
 *   (F) AccountabilityPack
 *
 * Style: W46 aesthetic — surface cards, teal accent, 5-color palette.
 * No Recharts. No emojis. No exclamation points. M-PACT hyphenated.
 * Tenant 1 only (enforced server-side).
 */

import { useState } from "react";
import { FunnelVisual } from "@/components/marketing-dashboard/FunnelVisual";
import { MarketingCalendar } from "@/components/marketing-dashboard/MarketingCalendar";
import { HookLibrary } from "@/components/marketing-dashboard/HookLibrary";
import { LinkedInDripCard } from "@/components/marketing-dashboard/LinkedInDripCard";
import { MarketingActivityAudit } from "@/components/marketing-dashboard/MarketingActivityAudit";
import { AccountabilityPack } from "@/components/marketing-dashboard/AccountabilityPack";

type SectionKey =
  | "funnel"
  | "calendar"
  | "hooks"
  | "linkedin"
  | "audit"
  | "accountability";

const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: "funnel",         label: "Funnel" },
  { key: "calendar",       label: "Calendar" },
  { key: "hooks",          label: "Hook Library" },
  { key: "linkedin",       label: "LinkedIn Drip" },
  { key: "audit",          label: "Activity Audit" },
  { key: "accountability", label: "Accountability" },
];

const SECTION_DESCRIPTIONS: Record<SectionKey, string> = {
  funnel:         "MQL to SQL to Opp to Won — current quarter conversion funnel.",
  calendar:       "Marketing events by month. Toggle quarter or full year.",
  hooks:          "Outreach hooks grouped by campaign theme, with status and last-fired date.",
  linkedin:       "Active LinkedIn drip campaigns and reach data.",
  audit:          "Demo Marketing's marketing write log — last 4 weeks.",
  accountability: "Marketing vs sales accountability matrix — MQL targets by account.",
};

export default function MarketingDashboardPage() {
  const [active, setActive] = useState<SectionKey>("funnel");

  return (
    <div className="flex flex-col gap-6">
      {/* Section nav */}
      <div className="flex overflow-x-auto gap-0 -mb-px border-b border-border">
        {SECTIONS.map((s) => {
          const isActive = active === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setActive(s.key)}
              className={`px-4 py-2 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap focus:outline-none ${
                isActive
                  ? "border-[#0F766E] text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              }`}
              aria-selected={isActive}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Section description */}
      <p className="text-[12px] text-muted-foreground -mt-3">
        {SECTION_DESCRIPTIONS[active]}
      </p>

      {/* Section content */}
      <div className="flex flex-col gap-6">
        {active === "funnel" && (
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4 md:p-6">
            <h2 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              MQL to SQL to Opp to Won
            </h2>
            <FunnelVisual />
          </div>
        )}

        {active === "calendar" && (
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4 md:p-6">
            <h2 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Marketing Calendar
            </h2>
            <MarketingCalendar />
          </div>
        )}

        {active === "hooks" && (
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4 md:p-6">
            <h2 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Hook Library
            </h2>
            <HookLibrary />
          </div>
        )}

        {active === "linkedin" && (
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4 md:p-6">
            <h2 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              LinkedIn Drip Campaigns
            </h2>
            <LinkedInDripCard />
          </div>
        )}

        {active === "audit" && (
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4 md:p-6">
            <h2 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Marketing Activity Audit — Demo Marketing, last 4 weeks
            </h2>
            <MarketingActivityAudit />
          </div>
        )}

        {active === "accountability" && (
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4 md:p-6">
            <h2 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Accountability Pack — Marketing vs Sales
            </h2>
            <AccountabilityPack />
          </div>
        )}
      </div>
    </div>
  );
}
